import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { PMField } from "../../../utils/PMField";
import { PMButton } from "../../../utils/PMButton";
import { PMDialog } from "../../../utils/PMDialog";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  TruckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import ModernStorageSelector from "../../../utils/Selector/ModernStorageSelector";
import { ReportItemLineEditorForm } from "./report-item-line-editor-form";

export const ReportItemConsumptionDialog = ({
  open,
  onClose,
  item,
  onSave,
  report,
  /** Skjul linje-redigering (kun lesbar modus) */
  lineEditDisabled = false,
  /** Vis slett med bekreftelse i samme dialog */
  canDeleteLine = false,
  onSaveLine,
  onDeleteLine,
  deleteInProgress = false,
}) => {
  const { t } = useTranslation();
  const [storageSelectorOpen, setStorageSelectorOpen] = useState(false);
  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    setDeleteConfirmOpen(false);
    setTabValue(0);
  }, [item?.id, open]);

  if (!item) return null;

  const getConsumptionStatus = () => {
    const quantityUsed = item.quantity_used || 0;
    if (quantityUsed > 0 && !item.taken_in_vehicle) return "used_on_job";
    if (item.taken_in_vehicle) return "taken_in_vehicle";
    if (item.not_used) return "not_used";
    return null;
  };

  // Auto-set max quantity when editing consumption (if item is picked and all quantity is used)
  const getInitialQuantityUsed = () => {
    const pickedQuantity = item.quantity || 0;
    // If item is already picked and has consumption status, default to max quantity
    if (item.picked) {
      const status = getConsumptionStatus();
      if (status === "used_on_job" || status === "taken_in_vehicle") {
        // If already has quantity_used, keep it; otherwise default to max (picked quantity)
        return item.quantity_used || pickedQuantity;
      }
    }
    // Otherwise use existing quantity_used or 0
    return item.quantity_used || 0;
  };

  const initialValues = {
    picked: item.picked || false,
    consumption_status: getConsumptionStatus() || "",
    quantity_used: getInitialQuantityUsed(),
    storage_id: item.storage_id || null,
  };

  const validationSchema = Yup.object({
    picked: Yup.boolean(),
    consumption_status: Yup.string().nullable(), // Status er valgfritt
    storage_id: Yup.number().when("picked", {
      is: (picked) => picked === true,
      then: (schema) => schema.required("Lager er påkrevd når produktet er plukket"),
      otherwise: (schema) => schema.nullable(),
    }),
    quantity_used: Yup.number().when(["consumption_status", "picked"], {
      is: (consumption_status, picked) => {
        // Valider quantity_used hvis status er valgt og produktet er plukket (ikke for "not_used")
        return picked && (consumption_status === "used_on_job" || consumption_status === "taken_in_vehicle");
      },
      then: (schema) => {
        const pickedQuantity = item.quantity || 0;
        return schema
          .required("Antall er påkrevd")
          .min(0, "Antall kan ikke være negativt") // Tillat 0 for "brukt på oppdrag"
          .max(pickedQuantity, `Antall kan ikke være større enn ${pickedQuantity} (plukket antall)`)
          .test(
            "max-when-quantity-is-one",
            "Når antall plukket er 1, kan ikke antall brukt/tatt med være større enn 1",
            function(value) {
              if (pickedQuantity === 1 && value !== null && value !== undefined) {
                return parseFloat(value) <= 1;
              }
              return true;
            }
          );
      },
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Ensure item has an ID
      if (!item || !item.id) {
        toast.error("Kunne ikke oppdatere: Item mangler ID");
        console.error("Item missing ID in dialog:", item);
        return;
      }

      const wasPicked = item.picked || false;
      const isPicked = values.picked || false;
      const originalQuantity = item.quantity || 0;
      
      // Determine quantity_used based on consumption_status
      let quantityUsed = null;
      if (values.consumption_status === "used_on_job" || values.consumption_status === "taken_in_vehicle") {
        quantityUsed = parseFloat(values.quantity_used) || 0;
      } else if (values.consumption_status === "not_used") {
        quantityUsed = 0; // Not used means 0 was used
      }
      
      // Validate quantity_used doesn't exceed picked quantity
      if (quantityUsed !== null && quantityUsed > originalQuantity) {
        toast.error(`Antall kan ikke være større enn ${originalQuantity} (plukket antall)`);
        setSubmitting(false);
        return;
      }
      
      // Special validation: if quantity is 1, quantity_used cannot be more than 1
      if (originalQuantity === 1 && quantityUsed !== null && quantityUsed > 1) {
        toast.error(`Når antall plukket er 1, kan ikke antall brukt/tatt med være større enn 1`);
        setSubmitting(false);
        return;
      }
      
      // Calculate quantity_not_used
      // If consumption_status is "not_used", all items go to handling list
      // If consumption_status is "used_on_job" or "taken_in_vehicle", remaining goes to handling list
      let quantityNotUsed = null;
      if (isPicked) {
        if (values.consumption_status === "not_used") {
          quantityNotUsed = originalQuantity;
        } else if (values.consumption_status === "used_on_job" || values.consumption_status === "taken_in_vehicle") {
          if (quantityUsed !== null) {
            quantityNotUsed = originalQuantity - quantityUsed;
            // Only set if there's actually something not used
            quantityNotUsed = quantityNotUsed > 0 ? quantityNotUsed : null;
          }
        }
      }

      // Map consumption_status to boolean fields
      const updatedItem = {
        ...item,
        id: item.id,
        picked: isPicked,
        picked_at: isPicked && !wasPicked ? new Date().toISOString() : (isPicked ? item.picked_at : null),
        quantity_picked: isPicked && !wasPicked ? originalQuantity : (isPicked ? item.quantity_picked : null),
        taken_in_vehicle: values.consumption_status === "taken_in_vehicle",
        not_used: values.consumption_status === "not_used",
        quantity_used: quantityUsed,
        quantity_not_used: quantityNotUsed,
        // Reset other statuses
        returned_to_storage: false,
        left_in_vehicle: false,
        // Preserve existing storage_id if not explicitly changed, otherwise use new value
        storage_id: values.storage_id !== undefined && values.storage_id !== null
          ? values.storage_id
          : (item.storage_id || null),
      };

      // Reset other statuses to false if not set
      if (values.consumption_status !== "taken_in_vehicle") {
        updatedItem.taken_in_vehicle = false;
      }
      if (values.consumption_status !== "not_used") {
        updatedItem.not_used = false;
      }

      const saved = await onSave(updatedItem);
      if (saved !== false) onClose();
    } catch (error) {
      console.error("Error updating item consumption:", error);
      toast.error("Kunne ikke oppdatere vareforbruk");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "used_on_job":
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case "taken_in_vehicle":
        return <TruckIcon className="h-5 w-5 text-blue-600" />;
      case "not_used":
        return <CheckCircleIcon className="h-5 w-5 text-gray-600" />;
      default:
        return null;
    }
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case "used_on_job":
        return "Produktet er brukt på oppdraget";
      case "taken_in_vehicle":
        return "Produktet er tatt med i bilen";
      case "not_used":
        return "Produktet er ikke brukt på oppdraget";
      default:
        return "";
    }
  };

  const originalQuantity = item.quantity || 0;
  const pickedQuantity = originalQuantity;

  const showDeleteControl =
    canDeleteLine && typeof onDeleteLine === "function";

  const isDeleteConfirmMode = deleteConfirmOpen && showDeleteControl;

  const dialogTitle = isDeleteConfirmMode
    ? "service.report.product_line_dialog.delete_confirm_title"
    : "service.report.product_line_dialog.title";

  const dialogHeaderActions = isDeleteConfirmMode
    ? [
        {
          title: "back-from-delete",
          component: (
            <Tooltip title={t("service.report.product_line_dialog.back_tooltip")}>
              <span className="inline-flex">
                <IconButton
                  aria-label={t("service.report.product_line_dialog.back_tooltip")}
                  onClick={() => !deleteInProgress && setDeleteConfirmOpen(false)}
                  disabled={deleteInProgress}
                  size="small"
                  className="-mr-1 text-gray-700"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </IconButton>
              </span>
            </Tooltip>
          ),
        },
      ]
    : showDeleteControl
      ? [
          {
            title: "delete-product-line",
            component: (
              <Tooltip title={t("service.report.product_line_dialog.delete_tooltip")}>
                <span className="inline-flex">
                  <IconButton
                    aria-label={t("service.report.product_line_dialog.delete_line")}
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={deleteInProgress}
                    color="error"
                    size="small"
                    className="-mr-1"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </IconButton>
                </span>
              </Tooltip>
            ),
          },
        ]
      : undefined;

  const lineLabel =
    item?.product?.displayTitle || item?.title || `Vare ${item?.id}`;

  return (
    <PMDialog
      title={dialogTitle}
      headerActions={dialogHeaderActions ?? []}
      open={open}
      onClose={onClose}
      size="lg"
      zIndex={2010}
    >
      {isDeleteConfirmMode ? (
        <div className="flex min-h-[min(320px,calc(85vh-14rem))] flex-col justify-center px-2 py-8 sm:py-12">
          <div className="mx-auto flex w-full max-w-lg flex-col items-center text-center">
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <TrashIcon className="h-9 w-9 text-red-600" aria-hidden />
            </div>
            <p className="text-base leading-relaxed text-gray-600">
              {t("service.report.product_line_dialog.delete_confirm_hint")}
            </p>
            <p className="mt-4 text-sm font-medium text-gray-900">{lineLabel}</p>
            <div className="mt-10 flex w-full max-w-md flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                variant="outlined"
                fullWidth
                className="sm:max-w-[11rem]"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteInProgress}
              >
                {t("service.report.product_line_dialog.delete_cancel")}
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                className="sm:max-w-[11rem]"
                disabled={deleteInProgress}
                onClick={() => onDeleteLine(item)}
              >
                {deleteInProgress
                  ? t("service.report.product_line_dialog.delete_working")
                  : t("service.report.product_line_dialog.delete_confirm")}
              </Button>
            </div>
          </div>
        </div>
      ) : (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Viktig: Velg riktig status
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Velg nøye hva som har skjedd med produktet. Dette påvirker
                  lagerbeholdningen og faktureringen.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              {item?.product?.displayTitle ? (
                <div className="text-lg font-semibold text-gray-900">
                  {item.product.displayTitle}
                </div>
              ) : (
                <div className="text-lg font-semibold text-gray-900">
                  {item.title || `Vare ${item.id}`}
                </div>
              )}
            </div>
          </div>
          {item?.product?.item_number && (
            <div className="text-sm text-gray-500 mb-4">
              Artikkelnummer: {item.product.item_number}
            </div>
          )}
          <div className="text-sm text-gray-700 mb-2">
            <strong>Plukket antall:</strong> {pickedQuantity} {item.unit?.title || ""}
          </div>
        </div>

        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab label="Vareforbruk" />
          <Tab
            label="Rediger"
            disabled={lineEditDisabled || typeof onSaveLine !== "function"}
          />
        </Tabs>

        <div className={tabValue === 0 ? "" : "hidden"} aria-hidden={tabValue !== 0}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => {
            // Store formik reference for use in Storage Selector Dialog
            window.currentFormik = formik;
            
            return (
            <>
            <Form>
              <div className="space-y-6">
                {/* Picked Status */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Plukket (klargjort for oppdrag)
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Marker produktet som klargjort og klar for bruk på oppdraget
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formik.values.picked}
                      onChange={(e) => {
                        formik.setFieldValue("picked", e.target.checked);
                        if (!e.target.checked) {
                          // Clear consumption status if unpicked
                          formik.setFieldValue("consumption_status", "");
                          formik.setFieldValue("quantity_used", 0);
                        }
                      }}
                      className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Storage selection - required when picked - moved right after picked section */}
                {formik.values.picked && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lager *
                    </label>
                    <div
                      className={`cursor-pointer border-2 rounded-lg p-3 transition-colors ${
                        formik.values.storage_id
                          ? "border-blue-300 bg-blue-50"
                          : formik.touched.storage_id && formik.errors.storage_id
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-blue-300"
                      }`}
                      onClick={() => setStorageSelectorOpen(true)}
                    >
                      {formik.values.storage_id ? (
                        <div>
                          <div className="font-medium text-blue-700">
                            {(() => {
                              const selectedStorage = storages.find(s => s.id === formik.values.storage_id);
                              
                              if (selectedStorage) {
                                return `${selectedStorage.storage_number ? `${selectedStorage.storage_number} - ` : ""}${selectedStorage.title || `Lager ${selectedStorage.id}`}`;
                              }
                              return `Lager ID: ${formik.values.storage_id}`;
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500 text-center">
                          <div className="text-sm font-medium">
                            + Klikk for å velge lager
                          </div>
                        </div>
                      )}
                    </div>
                    {formik.touched.storage_id && formik.errors.storage_id && (
                      <div className="text-red-600 text-xs mt-1">
                        {formik.errors.storage_id}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Velg hvilket lager produktet er plukket fra. Dette er påkrevd for å kunne spore lagerbeholdningen.
                    </p>
                  </div>
                )}

                {/* Show info about quantity when picked */}
                {formik.values.picked && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Viktig: Antall kan ikke endres
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Når produktet er plukket, kan ikke det faktiske antallet ({pickedQuantity} {item.unit?.title || ""}) endres.
                            Du kan kun registrere hvor mye som er brukt på oppdrag eller tatt med i bilen.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {formik.values.picked && (
                <div>
                  <FormLabel component="legend" className="text-sm font-medium text-gray-700 mb-3 block">
                    Status for vareforbruk
                  </FormLabel>
                  <RadioGroup
                    name="consumption_status"
                    value={formik.values.consumption_status}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      formik.setFieldValue("consumption_status", newStatus);
                      // Auto-set max quantity when selecting a status (if quantity_used is empty or 0)
                      if ((newStatus === "used_on_job" || newStatus === "taken_in_vehicle")) {
                        const currentQty = formik.values.quantity_used;
                        // Only set to max if it's empty, 0, or null
                        if (!currentQty || currentQty === 0 || currentQty === null) {
                          formik.setFieldValue("quantity_used", pickedQuantity);
                        }
                      } else if (newStatus === "not_used") {
                        formik.setFieldValue("quantity_used", null);
                      }
                    }}
                  >
                    <div className="space-y-3">
                      <div
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                          formik.values.consumption_status === "taken_in_vehicle"
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-blue-300"
                        }`}
                        onClick={() => {
                          formik.setFieldValue("consumption_status", "taken_in_vehicle");
                          // Auto-set max quantity when selecting status (if quantity_used is empty or 0)
                          if (!formik.values.quantity_used || formik.values.quantity_used === 0) {
                            formik.setFieldValue("quantity_used", pickedQuantity);
                          }
                        }}
                      >
                        <FormControlLabel
                          value="taken_in_vehicle"
                          control={<Radio />}
                          label={
                            <div className="flex items-center space-x-3 w-full">
                              {getStatusIcon("taken_in_vehicle")}
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  Tatt med i bilen
                                </div>
                                <div className="text-sm text-gray-500">
                                  {getStatusDescription("taken_in_vehicle")}
                                </div>
                                {formik.values.consumption_status === "taken_in_vehicle" && (
                                  <div className="mt-3">
                                    <PMField
                                      name="quantity_used"
                                      type="number"
                                      label="Antall tatt med"
                                      description={`Maksimalt ${pickedQuantity} ${item.unit?.title || ""}`}
                                      step={pickedQuantity === 1 ? "1" : "0.01"}
                                      min="0"
                                      max={pickedQuantity}
                                      {...formik}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          }
                        />
                      </div>

                      <div
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                          formik.values.consumption_status === "used_on_job"
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-green-300"
                        }`}
                        onClick={() => {
                          formik.setFieldValue("consumption_status", "used_on_job");
                          // Auto-set max quantity when selecting status (if quantity_used is empty or 0)
                          if (!formik.values.quantity_used || formik.values.quantity_used === 0) {
                            formik.setFieldValue("quantity_used", pickedQuantity);
                          }
                        }}
                      >
                        <FormControlLabel
                          value="used_on_job"
                          control={<Radio />}
                          label={
                            <div className="flex items-center space-x-3 w-full">
                              {getStatusIcon("used_on_job")}
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  Brukt på oppdraget
                                </div>
                                <div className="text-sm text-gray-500">
                                  {getStatusDescription("used_on_job")}
                                </div>
                                {formik.values.consumption_status === "used_on_job" && (
                                  <div className="mt-3">
                                    <PMField
                                      name="quantity_used"
                                      type="number"
                                      label="Antall brukt"
                                      description={`Maksimalt ${pickedQuantity} ${item.unit?.title || ""}`}
                                      step={pickedQuantity === 1 ? "1" : "0.01"}
                                      min="0"
                                      max={pickedQuantity}
                                      {...formik}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          }
                        />
                      </div>

                      <div
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                          formik.values.consumption_status === "not_used"
                            ? "border-gray-500 bg-gray-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => {
                          formik.setFieldValue("consumption_status", "not_used");
                          formik.setFieldValue("quantity_used", null);
                        }}
                      >
                        <FormControlLabel
                          value="not_used"
                          control={<Radio />}
                          label={
                            <div className="flex items-center space-x-3 w-full">
                              {getStatusIcon("not_used")}
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  Ikke brukt
                                </div>
                                <div className="text-sm text-gray-500">
                                  {getStatusDescription("not_used")}
                                </div>
                              </div>
                            </div>
                          }
                        />
                      </div>
                    </div>
                  </RadioGroup>
                  {formik.touched.consumption_status &&
                    formik.errors.consumption_status && (
                      <div className="text-red-600 text-xs mt-1">
                        {formik.errors.consumption_status}
                      </div>
                    )}
                </div>
                )}

                {/* Show warning if quantities don't match picked quantity (not for "not_used" status) */}
                {formik.values.picked && formik.values.consumption_status && formik.values.consumption_status !== "not_used" && (
                  (() => {
                    const usedQty = parseFloat(formik.values.quantity_used) || 0;
                    const remaining = pickedQuantity - usedQty;
                    
                    if (remaining > 0) {
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg
                                className="h-5 w-5 text-blue-600"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">
                                Resten håndteres senere
                              </h3>
                              <div className="mt-2 text-sm text-blue-700">
                                <p>
                                  Det er {remaining.toFixed(2)} {item.unit?.title || ""} som ikke er registrert som brukt.
                                  Disse ({remaining.toFixed(2)} {item.unit?.title || ""}) vil bli ført i en lagerhåndterings-liste for senere håndtering.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <PMButton
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    disabled={formik.isSubmitting}
                  >
                    Avbryt
                  </PMButton>
                  <PMButton
                    type="submit"
                    disabled={formik.isSubmitting}
                    loading={formik.isSubmitting}
                  >
                    Lagre
                  </PMButton>
                </div>
              </div>
            </Form>
            
            {/* Storage Selector Dialog - Inside Formik to access formik */}
            <Transition.Root show={storageSelectorOpen} as={Fragment}>
              <Dialog
                as="div"
                className="relative"
                style={{ zIndex: 2100 }}
                onClose={() => {
                  setStorageSelectorOpen(false);
                }}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" style={{ zIndex: 2100 }} />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 2100 }}>
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <Transition.Child
                      as={Fragment}
                      enter="ease-out duration-300"
                      enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                      enterTo="opacity-100 translate-y-0 sm:scale-100"
                      leave="ease-in duration-200"
                      leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                      leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                        <div className="h-[500px] border border-gray-200 rounded-lg overflow-hidden">
                          <ModernStorageSelector
                            open={storageSelectorOpen}
                            selectedStorage={formik?.values?.storage_id ? { id: formik.values.storage_id } : null}
                            onChange={(storage) => {
                              if (storage) {
                                formik.setFieldValue("storage_id", storage.id);
                                formik.setFieldTouched("storage_id", true);
                              } else {
                                formik.setFieldValue("storage_id", null);
                              }
                              setStorageSelectorOpen(false);
                            }}
                            onClose={() => {
                              setStorageSelectorOpen(false);
                            }}
                            multiple={false}
                            showHeader={true}
                          />
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </Dialog>
            </Transition.Root>
            </>
            );
          }}
        </Formik>
        </div>

        <div className={tabValue === 1 ? "" : "hidden"} aria-hidden={tabValue !== 1}>
          {!lineEditDisabled && typeof onSaveLine === "function" ? (
            <ReportItemLineEditorForm
              sourceItem={item}
              active={open}
              onSave={onSaveLine}
              submitLabel="Lagre linjedetaljer"
              showFooterCancel={false}
            />
          ) : (
            <p className="text-sm text-gray-500 py-4">Redigering av linjen er ikke tilgjengelig.</p>
          )}
        </div>
      </div>
      )}
    </PMDialog>
  );
};
